import { Col, Label, Row } from "reactstrap";
import { Controller as ReactHookFormController } from "react-hook-form";
import { Stack } from "react-bootstrap";
import { AntSekelton } from "@/Component/Skeleton";
import { LabelValue } from "@/Component/Value";

/**
 * Controller - A wrapper component around React Hook Form's Controller that provides
 * consistent form field layout, automatic error display, and flexible configuration.
 *
 * This component combines React Hook Form's Controller with Bootstrap/Reactstrap layout
 * components (Col, Stack, Label) to create a standardized form field structure with
 * built-in error handling.
 *
 * @param {Object} props - Component props
 * @param {Object} props.controllerProps - Props passed directly to React Hook Form Controller
 *   (name, control, rules, render, defaultValue, etc.)
 * @param {Object} props.labelProps - Props for the Label component (text, required, etc.)
 * @param {Object} props.layout - Layout configuration object
 * @param {Object} props.layout.colProps - Props for the Col component (xs, sm, md, lg, etc.)
 * @param {Object} props.layout.stackProps - Props for the Stack component (gap, direction, etc.)
 *
 * @example
 * // Basic text input with label
 * <Controller
 *   controllerProps={{
 *     name: "firstName",
 *     control: control,
 *     rules: { required: "First name is required" },
 *     render: ({ field }) => <Input {...field} type="text" />
 *   }}
 *   labelProps={{ text: "First Name", required: true }}
 *   layout={{
 *     colProps: { xs: 12, md: 6 },
 *     stackProps: {}
 *   }}
 * />
 *
 * @example
 * // Select dropdown with custom validation
 * <Controller
 *   controllerProps={{
 *     name: "country",
 *     control: control,
 *     rules: {
 *       required: "Please select a country",
 *       validate: (value) => value !== "none" || "Invalid selection"
 *     },
 *     render: ({ field }) => (
 *       <Select {...field}>
 *         <option value="none">Select Country</option>
 *         <option value="sa">Saudi Arabia</option>
 *         <option value="us">United States</option>
 *       </Select>
 *     )
 *   }}
 *   labelProps={{ text: "Country", required: true }}
 *   layout={{
 *     colProps: { xs: 12, sm: 6, lg: 4 },
 *     stackProps: { gap: 2 }
 *   }}
 * />
 *
 * @example
 * // Checkbox with custom render
 * <Controller
 *   controllerProps={{
 *     name: "agreeToTerms",
 *     control: control,
 *     defaultValue: false,
 *     render: ({ field: { value, onChange } }) => (
 *       <div>
 *         <input
 *           type="checkbox"
 *           checked={value}
 *           onChange={(e) => onChange(e.target.checked)}
 *         />
 *         <span>I agree to the terms and conditions</span>
 *       </div>
 *     )
 *   }}
 *   labelProps={{ text: "Agreement" }}
 *   layout={{
 *     colProps: { xs: 12 },
 *     stackProps: {}
 *   }}
 * />
 *
 * @example
 * // Date picker with full width layout
 * <Controller
 *   controllerProps={{
 *     name: "birthDate",
 *     control: control,
 *     rules: {
 *       required: "Birth date is required",
 *       validate: (value) => {
 *         const date = new Date(value);
 *         return date < new Date() || "Date must be in the past";
 *       }
 *     },
 *     render: ({ field }) => (
 *       <DatePicker
 *         {...field}
 *         selected={field.value}
 *         onChange={(date) => field.onChange(date)}
 *         dateFormat="yyyy-MM-dd"
 *       />
 *     )
 *   }}
 *   labelProps={{ text: "Birth Date", required: true }}
 *   layout={{
 *     colProps: { xs: 12, md: 6, lg: 4 },
 *     stackProps: { gap: 1 }
 *   }}
 * />
 *
 * @example
 * // Textarea with custom error styling
 * <Controller
 *   controllerProps={{
 *     name: "description",
 *     control: control,
 *     rules: {
 *       required: "Description is required",
 *       minLength: {
 *         value: 10,
 *         message: "Description must be at least 10 characters"
 *       }
 *     },
 *     render: ({ field }) => (
 *       <textarea
 *         {...field}
 *         rows={4}
 *         className="form-control"
 *         placeholder="Enter description..."
 *       />
 *     )
 *   }}
 *   labelProps={{ text: "Description", required: true }}
 *   layout={{
 *     colProps: { xs: 12 },
 *     stackProps: { gap: 1 }
 *   }}
 * />
 *
 * @example
 * // Radio button group
 * <Controller
 *   controllerProps={{
 *     name: "gender",
 *     control: control,
 *     rules: { required: "Please select a gender" },
 *     render: ({ field: { onChange, value } }) => (
 *       <div>
 *         <label>
 *           <input
 *             type="radio"
 *             value="male"
 *             checked={value === "male"}
 *             onChange={() => onChange("male")}
 *           />
 *           Male
 *         </label>
 *         <label>
 *           <input
 *             type="radio"
 *             value="female"
 *             checked={value === "female"}
 *             onChange={() => onChange("female")}
 *           />
 *           Female
 *         </label>
 *       </div>
 *     )
 *   }}
 *   labelProps={{ text: "Gender", required: true }}
 *   layout={{
 *     colProps: { xs: 12, md: 6 },
 *     stackProps: {}
 *   }}
 * />
 *
 * @returns {JSX.Element} A form field wrapped in Col, Stack, and Label components with automatic error display
 */
export const Controller = (props) => {
  const defaultLayoutValues = {
    colProps: { xs: 12, md: 6 },
    stackProps: {},
  };
  const {
    controllerProps,
    labelProps,
    layout = defaultLayoutValues,
    isLoading = false,
    isViewMode = false,
    renderValue = null,
    watch = null,
  } = props;
  const { colProps, stackProps } = layout;

  // Render Prop
  const hasRender = !!controllerProps?.render;
  const render = hasRender
    ? (props) => (
      <>
        {controllerProps?.render?.(props)}
        {controllerProps?.errors?.[controllerProps?.name] && (
          <div className="text-danger mt-1">
            {controllerProps?.errors[controllerProps?.name].message}
          </div>
        )}
      </>
    )
    : undefined;

  // ============
  // Render
  // ============
  if (isViewMode) {
    return (
      <Col {...colProps}>
        <Stack gap={1} className="mb-3" {...stackProps}>
          <Label
            {...labelProps}
            className={labelProps?.className?.replace("required", "")}
          />
          <div>
            {((renderValue && !isLoading) &&
              renderValue(
                watch ? watch(controllerProps?.name) : controllerProps?.value
              )) || (
                <LabelValue.Value
                  loading={isLoading}
                  value={watch ? watch(controllerProps?.name) : "-"}
                />
              )}
          </div>
        </Stack>
      </Col>
    );
  }
  if (isLoading) {
    return (
      <Col {...colProps}>
        <Stack gap={1} className="mb-3" {...stackProps}>
          <Label {...labelProps} />
          <AntSekelton.Input style={{ width: "100%" }} />
        </Stack>
      </Col>
    );
  }

  return (
    <Col {...colProps}>
      <Stack gap={1} className="mb-3" {...stackProps}>
        <Label {...labelProps} />
        <ReactHookFormController {...controllerProps} {...{ render }} />
      </Stack>
    </Col>
  );
};

export const Controllers = (props) => {
  const {
    controllers,
    isLoading = false,
    isViewMode = false,
    watch = null,
  } = props;

  return (
    <Row>
      {controllers.map((controller) => (
        <Controller
          key={controller.controllerProps?.name}
          isLoading={isLoading}
          isViewMode={controller?.controllerProps?.isViewMode !== undefined ? controller?.controllerProps?.isViewMode : isViewMode}
          watch={watch}
          {...controller}
        />
      ))}
    </Row>
  );
};
